import React from "react";
import { Space } from "antd";
import {
  AxDropdown,
  AxIconWrapper,
  AxButton,
  AxMenu,
  AxCheckbox,
  AxDivider,
} from "@dynata/axiom3";

const { SubMenu } = AxMenu;

class Dropdown extends React.Component {
  render() {
    return (
      <div className="section" id="dropdown">
        <h3>Dropdown</h3>
        <p>A dropdown list component</p>

        <Space size="large" direction="vertical">
          <AxDivider orientation="left" plain>
            Basic Usage
          </AxDivider>

          <Space size="large" direction="vertical">
            <pre>{`const { SubMenu } = AxMenu;
 <AxDropdown
   overlay={
     <AxMenu>
       <AxMenu.Item key="0">
         <AxIconWrapper type="regular" size="16" icon={"fa-star"} />
         List Item
       </AxMenu.Item>
       <AxMenu.Item key="1">
         <AxIconWrapper type="regular" size="16" icon={"fa-star"} />
         List Item
       </AxMenu.Item>
     </AxMenu>
   }
 >
   <AxButton size="medium" color="neutral" type="dropdown">
     Dropdown <i className="fa-regular fa-angle-down"></i>
   </AxButton>
 </AxDropdown>
`}</pre>

            <div className="prop-header">
              <p className="props-title">Props</p>
              <p>
                For AntD props, visit{" "}
                <AxButton
                  size="small"
                  type="link"
                  target="_blank"
                  href="https://ant.design/components/dropdown/#API"
                >
                  https://ant.design/components/dropdown/#API
                </AxButton>
              </p>
            </div>
            <table className="props-table">
              <thead>
                <tr>
                  <th>Property</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Default</th>
                  <th>Version</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>arrow</td>
                  <td>Whether the dropdown arrow should be visible</td>
                  <td>
                    boolean | <pre>{"{ pointAtCenter: boolean }"}</pre>
                  </td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>autoFocus</td>
                  <td>
                    Focus element in <code>overlay</code> when opened
                  </td>
                  <td>boolean</td>
                  <td>false</td>
                  <td>4.21.0</td>
                </tr>
                <tr>
                  <td>disabled</td>
                  <td>Whether the dropdown menu is disabled</td>
                  <td>boolean</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>destroyPopupOnHide</td>
                  <td>Whether destroy dropdown when hidden</td>
                  <td>boolean</td>
                  <td>false</td>
                  <td></td>
                </tr>
                <tr>
                  <td>getPopupContainer</td>
                  <td>
                    To set the container of the dropdown menu. The default is to
                    create a div element in body, but you can reset it to the
                    scrolling area and make a relative reposition.{" "}
                    <a href="https://codepen.io/afc163/pen/zEjNOy?editors=0010">
                      Example on CodePen
                    </a>
                  </td>
                  <td>(triggerNode: HTMLElement) =&gt; HTMLElement</td>
                  <td>() =&gt; document.body</td>
                  <td></td>
                </tr>
                <tr>
                  <td>overlay</td>
                  <td>The dropdown menu</td>
                  <td>
                    <a href="/components/menu/">Menu</a> | () =&gt; Menu
                  </td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>overlayClassName</td>
                  <td>The class name of the dropdown root element</td>
                  <td>string</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>overlayStyle</td>
                  <td>The style of the dropdown root element</td>
                  <td>CSSProperties</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>placement</td>
                  <td>
                    Placement of popup menu: <code>bottom</code>{" "}
                    <code>bottomLeft</code> <code>bottomRight</code>{" "}
                    <code>top</code> <code>topLeft</code> <code>topRight</code>
                  </td>
                  <td>string</td>
                  <td>
                    <code>bottomLeft</code>
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td>trigger</td>
                  <td>
                    The trigger mode which executes the dropdown action. Note
                    that hover can't be used on touchscreens
                  </td>
                  <td>
                    Array&lt;<code>click</code>|<code>hover</code>|
                    <code>contextMenu</code>&gt;
                  </td>
                  <td>
                    [<code>hover</code>]
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td>visible</td>
                  <td>Whether the dropdown menu is currently open.</td>
                  <td>boolean</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>onVisibleChange</td>
                  <td>
                    Called when the open state is changed. Not trigger when
                    hidden by click item.
                  </td>
                  <td>(open: boolean) =&gt; void</td>
                  <td>-</td>
                  <td></td>
                </tr>
                <tr>
                  <td>...others</td>
                  <td>
                    All extra props are passed down to the{" "}
                    <>
                      <AxButton
                        size="small"
                        type="link"
                        target="_blank"
                        href="https://4x.ant.design/components/dropdown/#API"
                      >
                        {`<Dropdown />`}
                      </AxButton>
                    </>{" "}
                    component.
                  </td>
                  <td></td>
                  <td>-</td>
                  <td></td>
                </tr>
              </tbody>
            </table>

            <Space>
              <AxDropdown
                overlay={
                  <AxMenu>
                    <AxMenu.Item key="2">
                      <AxIconWrapper
                        type="regular"
                        size="16"
                        icon={"fa-star"}
                      />
                      List Item
                    </AxMenu.Item>
                    <AxMenu.Item key="2b">List Item</AxMenu.Item>

                    <AxMenu.Divider />

                    <AxMenu.ItemGroup key="101" title="Group Title">
                      <AxMenu.Item key="102">Option 1</AxMenu.Item>
                      <AxMenu.Item key="103">Option 2</AxMenu.Item>
                    </AxMenu.ItemGroup>

                    <AxMenu.Divider />

                    <AxMenu.Item key="3" disabled>
                      Dropdown List Item
                    </AxMenu.Item>
                    <AxMenu.Item key="4" danger>
                      Dropdown List Item
                    </AxMenu.Item>

                    <SubMenu
                      popupClassName="ax-dropdown-submenu"
                      key="sub2"
                      title="Sub Menu"
                    >
                      <AxMenu.Item className="group-title">
                        Submenu Item
                      </AxMenu.Item>
                      <AxMenu.Item key="109">Submenu Item</AxMenu.Item>
                      <AxMenu.Item key="110">Submenu Item</AxMenu.Item>
                    </SubMenu>

                    <SubMenu
                      popupClassName="ax-dropdown-submenu"
                      key="sub3"
                      title="Sub Menu"
                      disabled
                    >
                      <AxMenu.Item className="group-title">
                        Submenu Item
                      </AxMenu.Item>
                      <AxMenu.Item key="112">Submenu Item</AxMenu.Item>
                      <AxMenu.Item key="113">Submenu Item</AxMenu.Item>
                    </SubMenu>

                    <AxMenu.Item key="5">Dropdown List Item</AxMenu.Item>
                  </AxMenu>
                }
              >
                <AxButton type="link" onClick={(e) => e.preventDefault()}>
                  Hover me
                </AxButton>
              </AxDropdown>

              <AxDropdown
                overlay={
                  <AxMenu>
                    <AxMenu.Item key="0">
                      <AxIconWrapper
                        type="regular"
                        size="16"
                        icon={"fa-star"}
                      />
                      List Item
                    </AxMenu.Item>
                    <AxMenu.Item key="1">
                      <AxIconWrapper
                        type="regular"
                        size="16"
                        icon={"fa-star"}
                      />
                      List Item
                    </AxMenu.Item>
                  </AxMenu>
                }
              >
                <AxButton size="medium" color="neutral" type="dropdown">
                  Dropdown <i className="fa-regular fa-angle-down"></i>
                </AxButton>
              </AxDropdown>

              <AxDropdown
                overlay={
                  <AxMenu>
                    <AxMenu.Item key="0">
                      <AxIconWrapper
                        type="regular"
                        size="16"
                        icon={"fa-star"}
                      />
                      List Item
                    </AxMenu.Item>
                    <AxMenu.Item key="1">
                      <AxIconWrapper
                        type="regular"
                        size="16"
                        icon={"fa-star"}
                      />
                      List Item
                    </AxMenu.Item>
                  </AxMenu>
                }
                trigger={["click"]}
                placement="topLeft"
                arrow
              >
                <AxButton size="medium" color="blue" type="primary">
                  Dropdown Click
                </AxButton>
              </AxDropdown>

              <AxDropdown
                trigger={["click"]}
                overlay={
                  <AxMenu>
                    <AxMenu.Item key="2">
                      <AxCheckbox>Checkbox</AxCheckbox>
                    </AxMenu.Item>
                    <AxMenu.Item key="2b">
                      <AxCheckbox>Checkbox</AxCheckbox>
                    </AxMenu.Item>

                    <AxMenu.Divider />

                    <AxMenu.ItemGroup key="101" title="Group Title">
                      <AxMenu.Item key="102">
                        <AxCheckbox>Checkbox</AxCheckbox>
                      </AxMenu.Item>
                      <AxMenu.Item key="103">
                        <AxCheckbox>Checkbox</AxCheckbox>
                      </AxMenu.Item>
                    </AxMenu.ItemGroup>

                    <AxMenu.Item key="3" disabled>
                      <AxCheckbox>Checkbox</AxCheckbox>
                    </AxMenu.Item>
                    <AxMenu.Item key="4" danger>
                      <AxCheckbox>Checkbox</AxCheckbox>
                    </AxMenu.Item>

                    <AxMenu.Item key="5">
                      <AxCheckbox>Checkbox</AxCheckbox>
                    </AxMenu.Item>
                  </AxMenu>
                }
              >
                <AxButton>Checkboxes</AxButton>
              </AxDropdown>

              {/* <AxDropdown
                overlay={
                  <AxMenu>
                    <AxMenu.Item key="2">
                      <AxFlag
                        country="US"
                        svgProps={{ width: 24, viewBox: "0 0 25 16" }}
                      />
                      United States
                    </AxMenu.Item>
                    <AxMenu.Item key="2b">
                      <AxFlag
                        country="AR"
                        svgProps={{ width: 24, viewBox: "0 0 25 16" }}
                      />
                      Argentina
                    </AxMenu.Item>

                    <AxMenu.Item key="2c">
                      <AxFlag
                        country="BR"
                        svgProps={{ width: 24, viewBox: "0 0 25 16" }}
                      />
                      Brazil
                    </AxMenu.Item>

                    <AxMenu.Item key="2d">
                      <AxFlag
                        country="CA"
                        svgProps={{ width: 24, viewBox: "0 0 25 16" }}
                      />
                      Canada
                    </AxMenu.Item>

                    <AxMenu.Item key="2e">
                      <AxFlag
                        country="CO"
                        svgProps={{ width: 24, viewBox: "0 0 25 16" }}
                      />
                      Colombia
                    </AxMenu.Item>
                    <AxMenu.Item key="2f">
                      <AxFlag
                        country="FR"
                        svgProps={{ width: 24, viewBox: "0 0 25 16" }}
                      />
                      France
                    </AxMenu.Item>
                  </AxMenu>
                }
                trigger={["click"]}
                size="large"
              >
                <AxButton size="medium" color="blue" type="primary">
                  Flags
                </AxButton>
              </AxDropdown> */}
            </Space>
          </Space>
        </Space>
      </div>
    );
  }
}
export default Dropdown;
